import * as d3 from 'd3';
import dayjs from 'dayjs';
import Helpers from '@client/helper/unit';
import { isNoValue } from '@client/helper/validate';
import { xss } from '@client/helper/string';
export class OverviewChart {
  STROKE_DASHARRAY = '8,8';

  constructor(options) {
    const that = this;
    that.el = options.el || null;

    // 主题
    that.theme = options.theme || 'theme-dark';

    that.margin = options.margin || null;
    that.width = that.el.clientWidth || 0;
    that.height = that.el.clientHeight || 0;

    // 整个图表
    that.svg = d3.select(that.el).append('svg').attr('width', that.width).attr('height', that.height);
    // 面积图渐变色
    let { linearGradients = [] } = options;
    this.defs = that.svg.append('defs');
    linearGradients.forEach((item) => {
      let { x1, x2, y1, y2 } = item.attr;
      this.defs
        .append('linearGradient')
        .attr('id', item.id)
        .attr('gradientUnits', 'userSpaceOnUse')
        .attr('x1', x1)
        .attr('y1', y1)
        .attr('x2', x2)
        .attr('y2', y2)
        .selectAll('stop')
        .data(item.datas)
        .enter()
        .append('stop')
        .attr('offset', function (d) {
          return d.offset;
        })
        .attr('stop-color', function (d) {
          return d.color;
        });
    });

    // 存放图表背景
    that.chartBg = that.svg.append('g').attr('class', 'chart-bg').attr('width', that.width).attr('height', that.height);

    // 存放标签图标
    that.labelArea = that.svg.append('g').attr('class', 'label-area').attr('transform', `translate(${that.margin.left}, 28)`);

    // 图表内容区域（不含坐标轴）
    that.content = that.svg
      .append('g')
      .attr('class', 'chart-content')
      .attr('transform', `translate(${that.margin.left}, ${that.margin.top})`);
    that.contentWidth = that.width - that.margin.left - that.margin.right;
    that.contentHeight = that.height - that.margin.top - that.margin.bottom;

    that.weekCount = options.weekCount || 52;

    // 每天的格子宽度
    that.dayWidth = that.contentWidth / (7 * that.weekCount - 1);

    // 用于存放面积图
    that.areaChartGroup = that.content.append('g').attr('class', 'area-chart');
    // 用于存放柱状图
    that.histogramChartGroup = that.content.append('g').attr('class', 'histogram-chart');
    // 用于存放折线图
    that.polylineChartGroup = that.content.append('g').attr('class', 'polyline-chart');

    that.xScale = null;
    that.xAxisKey = options.xAxisKey;
    // x轴刻度文字大小
    that.xTickFontSize = options.xTickFontSize >= 0 ? options.xTickFontSize : 12;
    that.xTicks = options.xTicks || [];
    that.xTicksObj = {};
    that.xTickInfo =
      options.xTickInfo ||
      ((value) => {
        return {
          text: value,
          value
        };
      });
    that.yScales = {};
    that.showingAttributes = [];

    // 需要展示的内容
    that.decorateList = options.decorateList || [];

    that.data = null;
    that.objData = null;

    // 用于十字线移动时回调（获取鼠标对应位置的数据）
    that.crossHairCallback = options.crossHairCallback || null;

    // 使用初始数据进行初始化
    that.setData(that.data);
    that.rootListener();

    that.setPolylineShadow();
  }

  /**
   * 主题颜色
   * @returns {*}
   */
  get themeStyle() {
    const that = this;
    const style = {
      'theme-dark': {
        xTooltipBg: '#3B3C44',
        xTooltipColor: '#ffffff',
        xScaleLineColor: 'rgba(222, 223, 225, 0.1)',
        xTickLineColor: '#999999',
        xTickTextColor: 'rgba(225, 225, 225, 0.8)',

        yScaleLineColor: '#999999',
        yScaleUnitTextColor: '#999999',
        yTickTextColor: '#999999',
        yTickLineColor: 'rgba(222, 223, 225, 0.1)',

        // 属性窗口
        attrTooltipBg: 'rgba(46, 56, 81, 0.9)',
        attrTooltipTitleColor: 'rgba(255, 255, 255, 0.6)',
        attrItemTextColor: 'rgba(255, 255, 255, 0.6)',
        attrItemActualTextColor: 'rgba(255, 255, 255, 1)',

        weekLabelGroupBg: '#29324A',
        labelDateColor: 'rgba(255, 255, 255, 0.6)',
        labelNameColor: 'rgba(255, 255, 255, 0.8)',

        stageLineBg: '#29324A',
        stageLineDateColor: 'rgba(255, 255, 255, 0.6)',
        stageLineNameColor: '#FFFFFF',

        crossHairHorizontalLineColor: '#999999',
        crossHairVerticalLineColor: '#999999'
      },
      'theme-light': {
        xTooltipBg: '#2ac595',
        xTooltipColor: '#ffffff',
        xScaleLineColor: 'rgba(222, 223, 225, 0.1)',
        xTickLineColor: '#999999',
        xTickTextColor: '#999999',

        yScaleLineColor: '#999999',
        yScaleUnitTextColor: '#999999',
        yTickTextColor: '#999999',
        yTickLineColor: '#dedfe1',

        // 属性窗口
        attrTooltipBg: '#ffffff',
        attrTooltipTitleColor: '#5e5e5e',
        attrItemTextColor: '#2e2e2e',
        attrItemActualTextColor: 'rgba(46, 46, 46, 0.74)',

        weekLabelGroupBg: '#ffffff',
        labelDateColor: 'rgba(46, 46, 46, 0.74)',
        labelNameColor: '#2e2e2e',

        stageLineBg: '#ffffff',
        stageLineDateColor: 'rgba(46, 46, 46, 0.74)',
        stageLineNameColor: '#2e2e2e',

        crossHairHorizontalLineColor: '#999999',
        crossHairVerticalLineColor: '#999999'
      }
    };
    const themeNames = Object.keys(style);
    if (themeNames.includes(that.theme)) {
      return style[that.theme];
    }
    return style[themeNames[0]];
  }

  /**
   * 创建用于折线图的阴影滤镜
   */
  setPolylineShadow() {
    const that = this;
    const polylineChartGroup = that.polylineChartGroup;

    const defs = polylineChartGroup.append('defs');

    const filter = defs.append('filter').attr('id', 'polyline-shadow').attr('height', '130%').attr('filterUnits', 'userSpaceOnUse');

    filter.append('feOffset').attr('in', 'SourceAlpha').attr('dx', 1).attr('dy', 2).attr('result', 'shadow-down');

    filter.append('feGaussianBlur').attr('in', 'shadow-down').attr('stdDeviation', 1).attr('result', 'shadow-down-result');

    const feMerge = filter.append('feMerge');

    feMerge.append('feMergeNode').attr('in', 'shadow-down-result');
    feMerge.append('feMergeNode').attr('in', 'SourceGraphic');
  }

  setBg(data) {
    const that = this;
    const yearTexts = {};

    for (let n = 0, length = data.length; n < length; n++) {
      const d = data[n];
      const rectWidth = that.xScale(d.endTimestamp) - that.xScale(d.startTimestamp);
      const monthItem = that.chartBg
        .append('g')
        .attr('class', 'month-item')
        .attr('transform', `translate(${that.xScale(d.startTimestamp)}, 0)`);

      const rect = monthItem
        .append('rect')
        .attr('class', 'month-rect')
        .attr('x', 0)
        .attr('y', 0)
        .attr('width', rectWidth)
        .attr('height', that.height)
        .attr('fill', d.color);

      const text = monthItem
        .append('text')
        .attr('class', 'month-text')
        .attr('x', 8)
        .attr('y', 5)
        .attr('fill', 'rgba(255, 255, 255, 0.4)')
        .attr('font-size', '12')
        .attr('dominant-baseline', 'text-before-edge')
        .attr('text-anchor', 'start')
        .text(() => {
          return d.text;
        });

      if (text._groups[0][0].getBoundingClientRect().width > rect._groups[0][0].getBoundingClientRect().width - 8) {
        text.attr('fill', 'rgba(255, 255, 255, 0)');
      }

      // 定义直线画法
      const lineFun = d3
        .line()
        .x((d, i) => {
          return d.x;
        })
        .y((d, i) => {
          return d.y;
        });

      monthItem
        .append('path')
        .attr('class', 'month-line')
        .attr(
          'd',
          lineFun([
            { x: 0, y: 27 },
            { x: rectWidth, y: 27 }
          ])
        )
        .attr('fill', 'none')
        .attr('stroke-width', 1)
        .attr('stroke', '#3F4353')
        .attr('stroke-dasharray', '8,8');

      // 年份文字
      if (d.yearText && !yearTexts[d.yearText]) {
        yearTexts[d.yearText] = {
          x: that.xScale(d.startTimestamp),
          y: 163,
          text: d.yearText
        };
        // monthItem
        //   .append('text')
        //   .attr('class', 'month-text')
        //   .attr('x', 8)
        //   .attr('y', 163)
        //   .attr('fill', 'rgba(255, 255, 255, 0.4)')
        //   .attr('font-size', `12`)
        //   .attr('dominant-baseline', 'text-before-edge')
        //   .attr('text-anchor', 'start')
        //   .text(() => {
        //     return d.yearText
        //   })
      }
    }

    for (const n in yearTexts) {
      const item = yearTexts[n];
      if (!item.text) {
        continue;
      }
      that.chartBg
        .append('text')
        .attr('class', 'month-text')
        .attr('x', item.x)
        .attr('y', item.y)
        .attr('fill', 'rgba(255, 255, 255, 0.4)')
        .attr('font-size', '12')
        .attr('dominant-baseline', 'text-before-edge')
        .attr('text-anchor', 'start')
        .text(() => {
          return item.text;
        });
    }
  }

  /**
   * 画图表
   * @param attr
   */
  drawChart(attr) {
    const that = this;
    switch (attr.chartType) {
      case 'planAndActualAreaLine':
        that.drawPlanAndActualAreaLineChart(attr);
        break;
      case 'planAndActualHistogram':
        that.drawPlanAndActualHistogramChart(attr);
        break;
    }
  }

  /**
   * 绘制周数据的面积折线图
   * @param attr
   */
  drawPlanAndActualAreaLineChart(attr) {
    const that = this;
    const { planValueKey, actualValueKey, valueKey, chartName, linearGradientId } = attr;
    const className = `attribute-${chartName}-chart`;
    const chartData = attr.chartData || that.chartData;
    const planColor = attr.planColor || 'rgba(184, 233, 255, 0.5)';
    const actualColor = attr.actualColor || '#00b3ff';

    let today = dayjs(new Date().getTime()).format('YYYY-MM-DD');
    today = new Date(today).getTime();

    // 定义面积画法
    const areaFun = d3
      .area()
      .defined(
        attr.defined ||
          ((d, i) => {
            // 控制是否绘制该点（return false不绘制）
            const value = d[valueKey][planValueKey];
            return !(isNaN(value) || isNaN(that.yScales[chartName](value)) || [undefined, ''].includes(value));
          })
      )
      .x((d, i) => {
        return that.xScale(d[that.xAxisKey]);
      })
      .y0((d, i) => {
        if (attr.y && attr.y instanceof Function) {
          return attr.y(d, i, that, attr);
        }
        return that.contentHeight - that.yScales[chartName](that.valueCorrect(attr, d[valueKey][planValueKey]));
      })
      .y1((d, i) => {
        return that.contentHeight;
      })
      .curve(attr.curve || d3.curveLinear);

    // 定义过去折线画法
    const foretimePolylineFun = d3
      .line()
      .defined(
        attr.defined ||
          ((d, i) => {
            // 控制是否绘制该点（return false不绘制）
            const value = d[valueKey][actualValueKey];
            return !(
              isNaN(value) ||
              isNaN(that.yScales[chartName](value)) ||
              [undefined, ''].includes(value) ||
              d[that.xAxisKey] > new Date().getTime()
            );
          })
      )
      .x((d, i) => {
        return that.xScale(d[that.xAxisKey]);
      })
      .y((d, i) => {
        if (attr.y && attr.y instanceof Function) {
          return attr.y(d, i, that, attr);
        }
        return that.contentHeight - that.yScales[chartName](that.valueCorrect(attr, d[valueKey][actualValueKey]));
      })
      .curve(attr.curve || d3.curveLinear);

    // 定义未来折线画法
    const futurePolylineFun = d3
      .line()
      .defined(
        attr.defined ||
          ((d, i) => {
            // 控制是否绘制该点（return false不绘制）
            const value = d[valueKey][actualValueKey];
            return !(isNaN(value) || isNaN(that.yScales[chartName](value)) || [undefined, ''].includes(value) || d[that.xAxisKey] < today);
          })
      )
      .x((d, i) => {
        return that.xScale(d[that.xAxisKey]);
      })
      .y((d, i) => {
        if (attr.y && attr.y instanceof Function) {
          return attr.y(d, i, that, attr);
        }
        return that.contentHeight - that.yScales[chartName](that.valueCorrect(attr, d[valueKey][actualValueKey]));
      })
      .curve(attr.curve || d3.curveLinear);

    let areaChart = that.areaChartGroup.select(`.${className}`);
    areaChart.remove();
    areaChart = that.areaChartGroup.append('g').attr('class', className);

    let polylineChart = that.polylineChartGroup.select(`.${className}`);
    polylineChart.remove();
    polylineChart = that.polylineChartGroup.append('g').attr('class', className);
    const fill = linearGradientId ? `url(#${linearGradientId})` : planColor;
    areaChart.append('path').attr('class', 'area').attr('d', areaFun(chartData)).attr('fill', fill);

    polylineChart
      .append('path')
      .attr('d', foretimePolylineFun(chartData))
      .attr('stroke', actualColor)
      .attr('stroke-width', 1)
      .attr('fill', 'none')
      .style('filter', 'url(#polyline-shadow)');

    polylineChart
      .append('path')
      .attr('d', futurePolylineFun(chartData))
      .attr('stroke', actualColor)
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '2,2')
      .attr('fill', 'none');
  }

  /**
   * 绘制周数据的柱状图
   * @param attr
   */
  drawPlanAndActualHistogramChart(attr) {
    const that = this;
    const chartName = attr.chartName;
    const className = `attribute-${chartName}-chart`;
    const chartData = attr.chartData || that.chartData;
    const rectWidth = that.dayWidth * 6 * 0.7;
    const rectMarginLeft = (that.dayWidth * 6 * 0.3) / 2;

    const valueKey = attr.valueKey;
    const planValueKey = attr.planValueKey;
    const actualValueKey = attr.actualValueKey;
    const planColor = attr.planColor || 'rgba(184, 233, 255, 0.5)';
    const actualColor = attr.actualColor || '#00b3ff';
    const overflowColor = attr.overflowColor || '#0088c2';
    const overflowLineColor = attr.overflowLineColor || 'rgba(255, 255, 255, 0.7)';

    let chart = that.histogramChartGroup.select(`.${className}`);
    chart.remove();
    chart = that.histogramChartGroup.append('g').attr('class', className);
    chartData.forEach((d, index) => {
      const weekGroup = chart.append('g').attr('class', 'week-group');

      const planValue = d[valueKey][planValueKey];
      const actualValue = d[valueKey][actualValueKey];

      // 柱的公共属性设置
      const setHistogramAttr = (select) => {
        return select
          .attr('class', 'rect')
          .attr('x', () => {
            return that.xScale(d[that.xAxisKey]) + rectMarginLeft;
          })
          .attr('width', rectWidth);
      };
      // 绘制计划柱状图
      if (!isNoValue(planValue) && planValue > 0) {
        setHistogramAttr(
          weekGroup
            .append('rect')
            .attr('class', 'plan-rect')
            .attr('y', () => {
              let height = 0;
              height = that.yScales[attr.chartName](that.valueCorrect(attr, planValue));
              if (height <= 0) {
                height = 0;
                // height = 1
              }
              return that.contentHeight - height - 1;
            })
            .attr('height', () => {
              let height = 0;
              height = that.yScales[attr.chartName](that.valueCorrect(attr, planValue));
              if (height <= 0) {
                height = 0;
                // height = 1
              }
              return height;
            })
            .attr('fill', that.getFillColor(planColor))
        );
      }

      // 今天之后的日期不需要绘制实际值
      if (d[that.xAxisKey] > new Date().getTime()) {
        return;
      }

      // 绘制实际柱状图
      if (!isNoValue(actualValue) && actualValue > 0) {
        setHistogramAttr(
          weekGroup
            .append('rect')
            .attr('class', 'actual-rect')
            .attr('y', () => {
              let height = 0;
              height = that.yScales[attr.chartName](that.valueCorrect(attr, actualValue));
              if (height <= 0) {
                height = 0;
                // height = 1
              }
              return that.contentHeight - height;
            })
            .attr('height', () => {
              let height = 0;
              height = that.yScales[attr.chartName](that.valueCorrect(attr, actualValue));
              if (height <= 0) {
                height = 0;
                // height = 1
              }
              return height;
            })
            .attr('fill', that.getFillColor(actualColor))
        );
      }

      if (!isNoValue(actualValue) && !isNoValue(planValue) && planValue > 0 && actualValue - planValue > 0) {
        // 绘制超出部分柱状图
        setHistogramAttr(
          weekGroup
            .append('rect')
            .attr('class', 'overflow-rect')
            .attr('y', () => {
              let height = 0;
              height = that.yScales[attr.chartName](that.valueCorrect(attr, actualValue));
              if (height <= 0) {
                height = 0;
                // height = 1
              }
              return that.contentHeight - height;
            })
            .attr('height', () => {
              let height = 0;
              height =
                that.yScales[attr.chartName](that.valueCorrect(attr, actualValue)) -
                that.yScales[attr.chartName](that.valueCorrect(attr, planValue));
              if (height <= 0) {
                height = 0;
                // height = 1
              }
              return height;
            })
            .attr('fill', that.getFillColor(overflowColor))
        );

        // 绘制超出的分割线
        setHistogramAttr(
          weekGroup
            .append('rect')
            .attr('class', 'overflowLine-rect')
            .attr('y', () => {
              let height = 0;
              height = that.yScales[attr.chartName](that.valueCorrect(attr, planValue));
              if (height <= 0) {
                height = 0;
                // height = 1
              }
              return that.contentHeight - height;
            })
            .attr('height', () => {
              return 1;
            })
            .attr('fill', that.getFillColor(overflowLineColor))
        );
      }
    });
  }

  /**
   * 监听图表根元素事件
   */
  rootListener() {
    const that = this;
    // 十字线
    d3.select(that.el)
      .on('mousemove', (e) => {
        requestAnimationFrame(() => {
          that.elMoveEvent(e);
        });
      })
      .on('mouseleave', (e) => {
        requestAnimationFrame(() => {
          that.deleteCrossHair();
          that.hideWeekStageLineLabel();
          that.hideWeekLabel();
        });
      })
      .on('touchstart', (e) => {
        e.preventDefault();
        requestAnimationFrame(() => {
          that.elMoveEvent(e?.touches?.[0]);
        });
      })
      .on('touchmove', (e) => {
        e.preventDefault();
        requestAnimationFrame(() => {
          that.elMoveEvent(e?.touches?.[0]);
        });
      });
  }

  /**
   * 取样之后的数据
   * @returns {null}
   */
  get chartData() {
    return this.data;
  }

  /**
   * 清空图表所有内容
   */
  remove() {
    const that = this;
    for (let n = that.el.childNodes.length - 1; n >= 0; n--) {
      that.el.removeChild(that.el.childNodes[n]);
    }
  }

  /**
   * 判断是否为不存在的元素
   * @param select
   * @returns {boolean}
   */
  isEmptySelect(select) {
    return [undefined, null].includes(select._groups[0][0]);
  }

  /**
   * 将颜色转换为rgba格式，change为变色方式，使用颜色前统一转换为rgba格式，解决出现一瞬间蓝色的问题
   * @param color
   * @param opacity
   * @returns {string|*}
   */
  getFillColor(color, opacity) {
    const activeColor = d3.color(color);
    if (!activeColor) {
      return color;
    }
    if (opacity) {
      activeColor.opacity = opacity;
    }
    for (const n in activeColor) {
      if (isNaN(activeColor[n])) {
        activeColor[n] = 0;
      }
    }
    return `rgba(${activeColor.r}, ${activeColor.g}, ${activeColor.b}, ${activeColor.opacity})`;
  }

  /**
   * 设置需要绘制的内容
   * @param list
   */
  setDecorate(list = []) {
    this.decorateList = list;
  }

  /**
   * 设置数据
   * @param data
   */
  setData(data) {
    if (!data) {
      return;
    }
    const that = this;
    // that.data = JSON.parse(JSON.stringify(data))
    that.data = data;
    that.objData = {};
    that.data.forEach((item) => {
      that.objData[item[that.xAxisKey]] = item;
    });
  }

  /**
   * 修正超出y坐标的数据为正常范围值
   * @param attr
   * @param value
   * @returns {*}
   */
  valueCorrect(attr, value) {
    // 若该属性有自己的数据修正逻辑，则优先使用
    if (attr.valueCorrect && attr.valueCorrect instanceof Function) {
      return attr.valueCorrect(attr, value);
    }
    const that = this;
    const chartName = attr.chartName;
    const bottomY = that.yScales[chartName].domain()[0];
    const topY = that.yScales[chartName].domain()[1];
    let result = value;

    // 正向y轴
    if (bottomY < topY) {
      if (result < bottomY) {
        result = bottomY;
      } else if (topY < result) {
        result = topY;
      }
    }

    // 反向y轴
    if (bottomY > topY) {
      if (result > bottomY) {
        result = bottomY;
      } else if (topY > result) {
        result = topY;
      }
    }

    return result;
  }

  /**
   * 创建x轴比例尺
   * @param domain
   * @param scaleType
   */
  createXAxis(domain, scaleType) {
    const that = this;
    that.xScale = d3[scaleType || 'scaleLinear']().domain(domain).range([0, that.contentWidth]);
  }

  /**
   * 设置x轴刻度文字大小
   * @param fontSize
   */
  setXTickFontSize(fontSize) {
    if (typeof fontSize !== 'number' || fontSize < 0) {
      return;
    }
    this.xTickFontSize = fontSize;
    this.drawXTickTexts();
    this.drawXTickImages();
  }

  /**
   * 生成X轴刻度文字
   */
  drawXTickTexts() {
    const that = this;
    const xAxis = that.svg.select('.x-axis');
    let xAxisTickTexts = xAxis.select('.x-axis-tick-texts');

    if (that.isEmptySelect(xAxis)) {
      return;
    }

    const dayWidth = that.dayWidth;

    xAxisTickTexts.remove();
    xAxisTickTexts = xAxis.append('g').attr('class', 'x-axis-tick-texts');
    xAxisTickTexts
      .selectAll('.x-tick-text')
      .data(that.xTicks)
      .enter()
      .filter((d) => {
        return !!d.text;
      })
      .append('text')
      .attr('class', 'x-tick-text')
      .attr('x', (d) => {
        return d.x + dayWidth * 3;
      })
      .attr('y', 13)
      .attr('fill', that.themeStyle.xTickTextColor)
      .attr('font-size', `${that.xTickFontSize}px`)
      .attr('dominant-baseline', 'text-before-edge')
      .attr('text-anchor', 'middle')
      .text((d) => {
        return d.text;
      });
  }

  /**
   * 生成X轴周标签图片
   */
  drawXTickImages() {
    const that = this;
    const dayWidth = that.dayWidth;
    if (!that.decorateList.includes('label')) {
      return;
    }

    // 标签图片
    that.labelArea
      .selectAll('.week-label-image')
      .data(that.xTicks)
      .enter()
      .filter((d, i) => {
        // 控制是否绘制该点（return false不绘制）
        if (!d.weekEventTags || !Array.isArray(d.weekEventTags) || d.weekEventTags.length === 0) {
          return false;
        }
        return true;
      })
      .append('image')
      .attr('class', 'week-label-image')
      .attr('x', (d) => {
        return d.x + dayWidth * 3 - 7;
      })
      .attr('y', 6)
      .attr('width', 14)
      .attr('xlink:href', (d) => {
        // return d.weekEventTags[d.weekEventTags.length - 1].image
        return d.weekEventImage;
      })
      .attr('data-week-num', (d) => {
        return d.weekNum;
      });

    that.drawWeekLabelList();
  }

  /**
   * 绘制周标签列表
   */
  drawWeekLabelList() {
    const that = this;
    let weekLabels = that.el.getElementsByClassName('week-labels')[0];

    if (!weekLabels) {
      weekLabels = document.createElement('div');
      weekLabels.className = 'week-labels';
      that.el.append(weekLabels);
    }

    weekLabels.innerHTML = '';
    if (!that.decorateList.includes('label')) {
      return;
    }

    // 内容样式
    const innerStyle = `
      <style>
          .week-label-group {
              position: absolute;
              top: 0;
              left: 0;
              padding: 8px 16px;
              border-radius: 2px;
              background-color: ${that.themeStyle.weekLabelGroupBg};
              display: flex;
              flex-direction: column;
              align-items: stretch;
              visibility: hidden;
              opacity: 0;
              transition: opacity 0.2s linear;
          }
          .label-item {
              padding-bottom: 8px;
              font-size: 14px;
          }
          .label-item:nth-last-of-type(1) {
              padding-bottom: 0;
          }
          .label-date {
              margin-bottom: 6px;
              white-space: nowrap;
              color: ${that.themeStyle.labelDateColor};
          }
          .label-content {
          }
          .label-content-item {
              margin-bottom: 4px;
              display: flex;
              align-items: center;
          }
          .label-content-item-icon {
              margin-right: 4px;
              max-width: 14px;
              max-height: 14px;
          }
          .label-content-item-name {
              min-width: 135px;
              max-width: 269px;
              font-size: 12px;
              color: ${that.themeStyle.labelNameColor};
              white-space: nowrap;
              text-overflow: ellipsis;
              overflow: hidden;
          }
      </style>
    `;

    let innerHtml = '';

    // 标签列表
    for (let n = 0, length = that.xTicks.length; n < length; n++) {
      const d = that.xTicks[n];

      if (!d.weekEventTags || !Array.isArray(d.weekEventTags) || d.weekEventTags.length === 0) {
        continue;
      }

      innerHtml += `
        <div class="week-label-group week-label-group-${d.weekNum}" data-week-num="${d.weekNum}">
      `;
      d.weekEventTags.forEach((week) => {
        const { format_date, data } = week;
        innerHtml += `
            <div class="label-item">
              <div class="label-date">${format_date}</div>
              <div class="label-content">`;

        data.forEach((label) => {
          const name = xss(label.name) || label.typeName;
          innerHtml += `
                <div class="label-content-item">
                    <img class="label-content-item-icon" src="${label.image}" >
                    <span class="label-content-item-name">${name}</span>
                </div>`;
        });

        innerHtml += `
              </div>
            </div>
        `;
      });

      innerHtml += `
        </div>
      `;
    }

    weekLabels.innerHTML = innerStyle + innerHtml;
  }

  // 展示周阶段连线对应的tip
  showWeekLabel(weekNum, x, y) {
    const that = this;
    const item = document.getElementsByClassName(`week-label-group-${weekNum}`)[0];

    // 出现位置
    const tipPositionInfo = that.getTipPositionInfo(x, y, item);

    item.style.transform = `translate(${tipPositionInfo.translateX}px, ${tipPositionInfo.translateY}px)`;
    item.style.visibility = 'visible';
    item.style.opacity = '1';
    item.style.zIndex = 3;
    item.className = `week-label-group week-label-group-${weekNum} ${tipPositionInfo.className}`;
  }

  // 隐藏周阶段连线tip
  hideWeekLabel() {
    const list = document.getElementsByClassName('week-label-group');

    for (let n = 0, length = list.length; n < length; n++) {
      const item = list[n];
      item.style.opacity = '0';
      item.style.visibility = 'hidden';
    }
  }

  /**
   * 绘制周阶段连续线条
   */
  drawWeekStageLine() {
    const that = this;
    const xAxis = that.svg.select('.x-axis');
    let xAxisWeekStages = xAxis.select('.x-axis-week-stages');

    if (that.isEmptySelect(xAxis)) {
      return;
    }

    const dayWidth = that.dayWidth;
    const rectWidth = dayWidth * 6 * 0.7;
    const rectMarginLeft = (dayWidth * 6 * 0.3) / 2;

    xAxisWeekStages.remove();

    if (!that.decorateList.includes('cyclePhase')) {
      return;
    }

    xAxisWeekStages = xAxis.append('g').attr('class', 'x-axis-week-stages');
    xAxisWeekStages
      .selectAll('.week-stage-color-line')
      .data(that.weekStageColorLine)
      .enter()
      .append('rect')
      .attr('class', 'week-stage-color-line')
      .attr('x', (d, i) => {
        return that.xScale(d.between[0]) + rectMarginLeft;
      })
      .attr('y', 2)
      .attr('width', (d, i) => {
        const start = that.xScale(d.between[0]);
        const end = that.xScale(d.between[1]);
        return end - start + rectWidth;
      })
      .attr('height', 4)
      .attr('fill', (d, i) => {
        return that.getFillColor(d.color);
      })
      .attr('data-line-num', (d, i) => {
        return i;
      });

    that.drawWeekStageLineLabelList();
  }

  drawWeekStageLineLabelList() {
    const that = this;

    // 标识分组
    let weekStageLines = that.el.getElementsByClassName('week-stage-lines')[0];

    if (!weekStageLines) {
      weekStageLines = document.createElement('div');
      weekStageLines.className = 'week-stage-lines';
      that.el.append(weekStageLines);
    }

    // 内容样式
    const innerStyle = `
      <style>
          .stage-line-item {
              position: absolute;
              top: 0;
              left: 0;
              padding: 12px;
              font-size: 14px;
              border-radius: 2px;
              background-color: ${that.themeStyle.stageLineBg};
              visibility: hidden;
              opacity: 0;
              transition: opacity 0.2s linear;
              z-index: 3;
          }
          .stage-line-date {
              min-width: 100px;
              margin-bottom: 6px;
              color: ${that.themeStyle.stageLineDateColor};
          }
          .stage-line-content {
              display: flex;
              align-items: baseline;
          }
          .stage-line-color {
              margin-right: 5px;
              width: 4px;
              height: 10px;
              border-radius: 2px;
          }
          .stage-line-name {
              max-width: 100px;
              color: ${that.themeStyle.stageLineNameColor};
              word-break: break-word;
          }
      </style>
    `;

    let innerHtml = '';

    that.weekStageColorLine.forEach((item) => {
      innerHtml += `
        <div class="stage-line-item">
            <div class="stage-line-date">${item.weekBetweenText}</div>
            <div class="stage-line-content">
                <span class="stage-line-color" style="background-color: ${item.color};"></span>
                <span class="stage-line-name">${item.name}</span>
            </div>
        </div>
      `;
    });

    weekStageLines.innerHTML = innerStyle + innerHtml;
  }

  // 展示周阶段连线对应的tip
  showWeekStageLineLabel(lineNum, x, y) {
    const that = this;
    const item = document.getElementsByClassName('stage-line-item')[lineNum];

    // 出现位置
    const tipPositionInfo = that.getTipPositionInfo(x, y, item);

    item.style.visibility = 'visible';
    item.style.opacity = '1';
    item.style.transform = `translate(${tipPositionInfo.translateX}px, ${tipPositionInfo.translateY}px)`;

    item.className = `stage-line-item ${tipPositionInfo.className}`;
  }

  // 隐藏周阶段连线tip
  hideWeekStageLineLabel() {
    const list = document.getElementsByClassName('stage-line-item');

    for (let n = 0, length = list.length; n < length; n++) {
      const item = list[n];
      item.style.opacity = '0';
      item.style.visibility = 'hidden';
    }
  }

  /**
   * 画出x轴
   * @param ticks
   * @param weekStageColorLine
   */
  drawXScale(ticks = [], weekStageColorLine = []) {
    const that = this;
    // 定义直线画法
    const lineFun = d3
      .line()
      .x((d, i) => {
        return d.x;
      })
      .y((d, i) => {
        return d.y;
      });

    if (!that.xScale) {
      console.error(new Error('绘制x轴需要先创建x轴比例尺'));
      return;
    }

    let xAxis = that.svg.select('.x-axis');
    // 没有创建过x轴元素
    xAxis.remove();
    xAxis = that.svg
      .append('g')
      .attr('class', 'x-axis')
      .attr('transform', `translate(${that.margin.left}, ${that.margin.top + that.contentHeight})`);

    // 绘制x轴横线
    xAxis
      .append('path')
      .attr('class', 'x-axis-line')
      .attr(
        'd',
        lineFun([
          { x: 0, y: 0 },
          { x: that.contentWidth, y: 0 }
        ])
      )
      .attr('fill', 'none')
      .attr('stroke-width', 1)
      .attr('stroke', that.getFillColor(that.themeStyle.xScaleLineColor));
    // .attr('stroke-dasharray', that.STROKE_DASHARRAY)

    that.xTicks = JSON.parse(JSON.stringify(ticks)).map((item, index) => {
      item.x = that.xScale(item[that.xAxisKey]);
      that.xTicksObj[item[that.xAxisKey]] = item;
      return item;
    });
    that.weekStageColorLine = weekStageColorLine;

    // 生成X轴刻度文字
    that.drawXTickTexts();
    that.drawXTickImages();
    that.drawWeekStageLine();
  }

  /**
   * 使用比例尺反向获取该坐标对应的值
   * @param x
   * @returns {number}
   */
  getValueFromXScale(x) {
    return Math.round(this.xScale.invert(x));
    // return Math.floor(this.xScale.invert(x))
  }

  /**
   * 创建y轴比例尺
   * @param attr
   */
  createYAxis(attr) {
    const that = this;

    const chartName = attr.chartName;
    const data = that.data.map((item) => {
      return item[attr.valueKey];
    });
    let min = Object.prototype.hasOwnProperty.call(attr, 'min') ? attr.min : d3.min(data) * 0.9;
    let max = Object.prototype.hasOwnProperty.call(attr, 'max') ? attr.max : d3.max(data) * 1.1;
    min = [undefined].includes(min) ? 0 : min;
    max = [undefined].includes(max) ? (min + 1) * 2 : max;
    if (min === max) {
      max = (min + 1) * 2;
    }

    that.yScales[chartName] = d3.scaleLinear().domain([min, max]).range([0, that.contentHeight]);
  }

  /**
   * 画出y轴
   * @param attrs
   * @param scaleType
   */
  drawYScale(attrs = [], scaleType) {
    const that = this;
    attrs.forEach((attr, index) => {
      that.createYAxis(attr);
    });
  }

  /**
   * 统一属性分组命名格式
   * @param attr
   * @returns {string}
   */
  attrGroupClassName(attr) {
    return `attribute-${attr.chartName}-chart`;
  }

  /**
   * 设置需要读取的属性
   * @param attributes
   */
  setShowingAttributes(attributes = []) {
    const that = this;
    that.yScales = {};
    that.showingAttributes = attributes;
    that.drawYScale(attributes);
    for (let n = 0, length = attributes.length; n < length; n++) {
      const attr = attributes[n];
      // 画图
      that.drawChart(attr);
    }
  }

  /**
   * 获取文本元素大小
   * @param text
   * @param fontSize
   * @returns {{width: number, height: number}}
   */
  getTextEleSize(text, fontSize = '12') {
    const ele = document.createElement('text');
    ele.style.fontSize = `${fontSize}px`;
    ele.textContent = text;
    document.body.append(ele);

    const rectInfo = ele.getBoundingClientRect();
    document.body.removeChild(ele);
    return {
      width: rectInfo.width,
      height: rectInfo.height
    };
  }

  /**
   * 绘制属性Tooltip
   * @param x
   * @param y
   * @param info
   */
  drawAttrTooltip(x, y, info = {}) {
    const that = this;
    let attributeTooltip = that.el.getElementsByClassName('chart-attribute-tooltip')[0];

    if (!attributeTooltip) {
      attributeTooltip = document.createElement('div');
      attributeTooltip.className = 'chart-attribute-tooltip';
      that.el.append(attributeTooltip);
    }

    const dayAttr = [];
    const weekAttr = [];

    that.showingAttributes.forEach((item) => {
      if (item.tooltipType === 'day') {
        dayAttr.push(item);
        return;
      }
      if (item.tooltipType === 'week') {
        weekAttr.push(item);
      }
    });

    // 生成内容

    // 内容样式
    const innerStyle = `
      <style>
          .chart-attribute-tooltip {
              pointer-events: none;
              position: absolute;
              top: 0;
              left: 0;
              display: flex;
              flex-direction: row;
              flex-wrap: nowrap;
              align-items: stretch;
              border-radius: 2px;
              outline: none;
              background-color: ${that.themeStyle.attrTooltipBg};
              box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.58);
              transform: translate(0, 0);
              z-index: 3;
          }

          .chart-attribute-tooltip .info-item {
              min-width: 190px;
              padding: 8px 15px 10px 15px;
              box-sizing: border-box;
          }

          .chart-attribute-tooltip .info-item .title {
              font-size: 14px;
              color: ${that.themeStyle.attrTooltipTitleColor};
          }

          .chart-attribute-tooltip .info-item .attributes .attribute-info {
              display: flex;
              flex-wrap: nowrap;
              align-items: center;
              box-sizing: border-box;
              padding: 3px 0px;

              font-size: 14px;
              color: ${that.themeStyle.attrItemTextColor};
          }

          .chart-attribute-tooltip .info-item .attributes .day-color {
              width: 4px;
              height: 4px;
              border-radius: 4px;
          }

          .chart-attribute-tooltip .info-item .attributes .week-color {
              width: 6px;
              height: 14px;
              border-radius: 1px;
          }

          .chart-attribute-tooltip .info-item .attributes .name {
              margin-left: 5px;
          }

          .chart-attribute-tooltip .info-item .attributes .actual {
              font-size: 14px;
              color: ${that.themeStyle.attrItemActualTextColor};
          }

          .chart-attribute-tooltip .info-item .attributes .line {
              margin-left: 5px;
          }

          .chart-attribute-tooltip .info-item .attributes .plan {
              margin-left: 5px;
          }

          .chart-attribute-tooltip .gap-line {
              margin: 8px 0;
              width: 1px;
              background-color: rgba(255, 255, 255, 0.1);
          }
      </style>
    `;

    let innerHtml = '';

    // 天数据
    if (dayAttr.length > 0) {
      innerHtml += `
        <div class="info-item">
            <div class="title">${info.dateNumText}</div>
            <div class="attributes">
      `;

      dayAttr.forEach((attr) => {
        const dayData = info[attr.valueKey];
        const actualValue = dayData[attr.actualValueKey];
        const planValue = dayData[attr.planValueKey];
        innerHtml += `
          <div class="attribute-info">
              <span class="day-color" style="background-color: ${dayData.color};"></span>
              <span class="name">${attr.name}：</span>`;

        innerHtml += `
              <span class="actual">${attr.valueText ? attr.valueText(actualValue) : actualValue}</span>
              <span class="line">/</span>
              <span class="plan">${attr.valueText ? attr.valueText(planValue) : planValue}</span>`;

        innerHtml += `
          </div>
        `;
      });

      innerHtml += `
            </div>
        </div>
      `;
    }

    // 分割线
    if (dayAttr.length > 0 && weekAttr.length > 0) {
      innerHtml += `
        <div class="gap-line"></div>
      `;
    }

    // 周数据
    if (weekAttr.length > 0) {
      const weekNum = info.weekNum - 1;
      innerHtml += `
        <div class="info-item">
            <div class="title">${info.weekNumText}</div>
            <div class="attributes">
      `;

      weekAttr.forEach((attr) => {
        const weekData = attr.chartData[weekNum][attr.valueKey];
        const actualValue = weekData[attr.actualValueKey];
        const planValue = weekData[attr.planValueKey];
        innerHtml += `
          <div class="attribute-info">
                <span class="week-color" style="background-color: ${attr.color};"></span>
                <span class="name">${attr.name}：</span>`;

        if (isNoValue(actualValue)) {
          // 计划的图表
          innerHtml += `
                <span class="plan">${attr.valueText ? attr.valueText(planValue) : planValue}</span>`;
          // 日程的图表
        } else if (actualValue === 0 && planValue === 0) {
          innerHtml += `
                <span class="actual">${attr.valueText ? attr.valueText(actualValue) : actualValue}</span>`;
        } else {
          innerHtml += `
                <span class="actual">${attr.valueText ? attr.valueText(actualValue) : actualValue}</span>
                <span class="line">/</span>
                <span class="plan">${attr.valueText ? attr.valueText(planValue) : planValue}</span>`;
        }

        innerHtml += `
          </div>
        `;
      });
      innerHtml += `
            </div>
        </div>
      `;
    }
    attributeTooltip.innerHTML = innerStyle + innerHtml;

    // 出现位置
    const tipPositionInfo = that.getTipPositionInfo(x, y, attributeTooltip);

    attributeTooltip.className = `chart-attribute-tooltip ${tipPositionInfo.className}`;

    attributeTooltip.style.opacity = '1';
    attributeTooltip.style.transform = `translate(${tipPositionInfo.translateX}px, ${tipPositionInfo.translateY}px)`;
  }

  /**
   * 删除属性Tooltip
   */
  removeAttrTooltip() {
    const that = this;
    const attributeTooltip = that.el.getElementsByClassName('chart-attribute-tooltip');
    if (that.el && attributeTooltip && attributeTooltip[0]) {
      attributeTooltip[0].remove();
    }
  }

  /**
   * 根据鼠标位置和元素，获取位置和箭头信息
   * @param x
   * @param y
   * @param tipEle
   * @returns {{translateY: number, translateX: number, className: string}}
   */
  getTipPositionInfo(x, y, tipEle) {
    const that = this;

    // 出现位置
    // const contentWidth = that.el.clientWidth
    // const contentHeight = that.el.clientHeight
    const contentWidth = that.contentWidth;
    const contentHeight = that.contentHeight;
    const offsetWidth = tipEle.offsetWidth;
    const offsetHeight = tipEle.offsetHeight;
    let translateX = 20;
    let translateY = 15;
    let className = '';

    if (x <= contentWidth / 2 && y <= contentHeight / 2) {
      // translateX = translateX
      translateY = -translateY;
      className = 'left-top-arrow';
    } else if (x > contentWidth / 2 && y <= contentHeight / 2) {
      translateX = -translateX - offsetWidth;
      translateY = -translateY;
      className = 'right-top-arrow';
    } else if (x <= contentWidth / 2 && y > contentHeight / 2) {
      // translateX = translateX
      translateY = translateY - offsetHeight;
      className = 'left-bottom-arrow';
    } else if (x > contentWidth / 2 && y > contentHeight / 2) {
      translateX = -translateX - offsetWidth;
      translateY = translateY - offsetHeight;
      className = 'right-bottom-arrow';
    }

    return {
      translateX: x + that.margin.left + translateX,
      translateY: y + that.margin.top + translateY,
      className
    };
  }

  /**
   * 获取数据中最接近的x的值
   * @param value
   * @returns {*}
   */
  getSimilarXValue(value) {
    const that = this;
    let result = 0;
    let num = 0;
    if (that.data.length < 1000) {
      return Helpers.getNearlyDataByBinarySearch({
        originalData: that.data,
        key: that.xAxisKey,
        value
      }).data[that.xAxisKey];
    }
    for (;;) {
      if (that.objData[value - num]) {
        result = that.objData[value - num][that.xAxisKey];
        break;
      }
      if (that.objData[value + num]) {
        result = that.objData[value + num][that.xAxisKey];
        break;
      }
      num++;
    }
    return result;
  }

  /**
   * 绘制十字线的横竖线
   * @param x
   * @param y
   */
  drawCrossHairLine(x, y) {
    const that = this;
    const drawHorizontalLine = false;
    const drawVerticalLine = true;

    // 定义直线画法
    const lineFun = d3
      .line()
      .x((d, i) => {
        return d.x;
      })
      .y((d, i) => {
        return d.y;
      });

    let crossHair = that.svg.select('.cross-hair');
    if (that.isEmptySelect(crossHair)) {
      crossHair = that.svg
        .append('g')
        .attr('class', 'cross-hair')
        .attr('transform', `translate(${that.margin.left}, ${that.margin.top})`)
        .style('pointer-events', 'none');
    }
    const horizontalLine = crossHair.select('.horizontal-line');
    const verticalLine = crossHair.select('.vertical-line');

    // 横线
    horizontalLine.remove();
    if (drawHorizontalLine) {
      crossHair
        .append('path')
        .attr('class', 'horizontal-line')
        .attr(
          'd',
          lineFun([
            { x: 0, y },
            { x: that.contentWidth, y }
          ])
        )
        .attr('pointer-events', 'none')
        .attr('fill', 'none')
        .attr('stroke-width', 1)
        .attr('stroke', that.themeStyle.crossHairHorizontalLineColor)
        .attr('stroke-dasharray', that.STROKE_DASHARRAY);
    }

    // 竖线
    if (that.isEmptySelect(verticalLine)) {
      verticalLine.remove();
      if (drawVerticalLine) {
        crossHair
          .append('path')
          .attr('class', 'vertical-line')
          .attr(
            'd',
            lineFun([
              { x: 0, y: 0 },
              { x: 0, y: that.contentHeight }
            ])
          )
          .attr('transform', `translate(${x}, 0)`)
          .attr('pointer-events', 'none')
          .attr('fill', 'none')
          .attr('stroke-width', 1)
          .attr('stroke', that.themeStyle.crossHairVerticalLineColor)
          .attr('stroke-dasharray', that.STROKE_DASHARRAY);
      }
    } else {
      verticalLine.attr('transform', `translate(${x}, 0)`);
    }
  }

  isIncludeElList(elList, el) {
    let result = false;
    for (let n = 0, length = elList.length; n < length; n++) {
      if (elList[n] === el) {
        result = true;
        break;
      }
    }
    return result;
  }

  elMoveEvent(e) {
    const that = this;

    const clientRect = that.el.getBoundingClientRect();
    const x = e.clientX - clientRect.left - that.margin.left;
    const y = e.clientY - clientRect.top - that.margin.top;

    const pointEle = document.elementFromPoint(e.clientX, e.clientY);

    // svg元素上
    if (that.isIncludeElList(document.getElementsByClassName('week-stage-color-line'), pointEle)) {
      that.deleteCrossHair();
      that.hideWeekLabel();
      that.showWeekStageLineLabel(pointEle.dataset.lineNum, x, y);
      return;
    } else if (that.isIncludeElList(document.getElementsByClassName('week-label-image'), pointEle)) {
      // 标签图片
      that.deleteCrossHair();
      that.hideWeekStageLineLabel();

      that.showWeekLabel(pointEle.dataset.weekNum, x, y);
      return;
    }
    that.hideWeekStageLineLabel();
    that.hideWeekLabel();

    if (x < 0 || x > that.contentWidth) {
      that.deleteCrossHair();
      return;
    }
    if (y < 0 || y > that.contentHeight) {
      that.deleteCrossHair();
      return;
    }

    this.drawCrossHair(e);
  }

  /**
   * 十字线
   * @param e
   */
  drawCrossHair(e) {
    const that = this;

    const clientRect = that.el.getBoundingClientRect();
    let x = e.clientX - clientRect.left - that.margin.left;
    const y = e.clientY - clientRect.top - that.margin.top;

    // 使用比例尺反向获取该坐标对应的值，并返回与该值最接近的数据值
    const xm = that.getSimilarXValue(this.getValueFromXScale(x));
    // 获取对应改x坐标的数据
    const xValueInfo = that.objData[xm];

    if (!xValueInfo) {
      return;
    }
    x = that.xScale(xValueInfo[that.xAxisKey]);

    if (that.crossHairCallback) {
      that.crossHairCallback(e, xValueInfo);
    }

    // 绘制十字线的横竖线
    that.drawCrossHairLine(x, y);
    // 绘制属性Tooltip
    that.drawAttrTooltip(x, y, xValueInfo);
  }

  /**
   * 清除十字线
   */
  deleteCrossHair() {
    const that = this;
    const crossHair = that.svg.select('.cross-hair');
    crossHair.remove();
    // 删除Tooltip
    if (
      that.el &&
      that.el.getElementsByClassName('chart-attribute-tooltip') &&
      that.el.getElementsByClassName('chart-attribute-tooltip')[0]
    ) {
      that.el.getElementsByClassName('chart-attribute-tooltip')[0].remove();
    }
  }
}
